import React, { useEffect, useState } from "react";

import { useLocation, useNavigate } from "react-router-dom";
import { FaLock } from "react-icons/fa";
import { confirmSignUp, resendSignUpCode, signIn, fetchAuthSession } from "../../auth/cognito";

function ConfirmSignup() {
  const location = useLocation();
  const navigate = useNavigate();

  // Values passed from RegisterForm
  const { userId, username, email, password, accountType, businessID } = location.state || {};

  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");

  useEffect(() => {
    // If someone hits this page directly without state, send them back.
    if (!username) {
      navigate("/register", { replace: true });
    }
  }, [username, navigate]);

  const clearMessages = () => {
    setError("");
    setStatus("");
  };

  const normalizeRole = (acctType) => {
    if (acctType === "business") return "unverified-business";
    if (acctType === "customer") return "customer";
    if (acctType === "unverified-business") return "unverified-business";
    return acctType || "";
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    clearMessages();
    setBusy(true);

    try {
      await confirmSignUp(username, code);
      setStatus("Confirmed. Signing you in...");

      // Sign them in immediately after confirmation (optional but common).
      // If you don't want to carry password in route state, ask them to re-enter it here instead.
      if (password) {
        await signIn({ username, password });
      } else {
        // If no password, redirect to login to enter it
        setStatus("Confirmed. Heading to login...");
        setTimeout(() => {
          navigate("/", {
            replace: true,
            state: { username },
          });
        }, 2000);
      }

      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      const role = normalizeRole(accountType);

      sessionStorage.setItem(
        "ftlUser",
        JSON.stringify({
          userID: userId,     // keep this consistent everywhere (ProfilePage expects user.userID)
          username,
          email,
          role,
          token,
        })
      );
      // Continue based on account type
      if (role === "business") {
        navigate("/business-verification", {
          replace: true,
          state: { userID: userId, username, email, role },
        });
      } else {
        navigate("/dashboard", {
          replace: true,
          state: { userID: userId, username, email, role, businessID },
        });
      }
    } catch (err) {
      console.error("Confirm sign-up error:", err);
      setError(err?.message || "Confirmation failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    clearMessages();
    setBusy(true);

    try {
      await resendSignUpCode(username);
      setStatus("A new confirmation code has been sent.");
    } catch (err) {
      console.error("Resend code error:", err);
      setError(err?.message || "Could not resend code.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="wrapper">
      <h1>Lowk</h1>
      <h2>Confirm Your Email</h2>

      <p className="hint">
        Enter the confirmation code sent to <strong>{email || "your email"}</strong>.
      </p>

      {error && <p className="error">{error}</p>}
      {status && <div className="status">{status}</div>}

      <form onSubmit={handleConfirm}>
        <div className="input-box">
          <input
            type="text"
            placeholder="Confirmation code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
            disabled={busy}
          />
          <FaLock className="icon" />
        </div>

        <button type="submit" disabled={busy || !code}>
          {busy ? "Confirming..." : "Confirm"}
        </button>

        <button
          type="button"
          className="linkish"
          onClick={handleResend}
          disabled={busy}
        >
          Resend code
        </button>

        <button
          type="button"
          className="linkish"
          onClick={() => navigate("/register", {
            state: { userId, username, email, password, accountType, businessID, fromConfirm: true }
          })}
          disabled={busy}
          style={{ marginTop: '10px' }}
        >
          Entered wrong email? Edit email
        </button>
      </form>
    </div>
  );
}

export default ConfirmSignup;
